import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import moment from 'moment';
import { io } from 'socket.io-client';
import { ticksBatchReceived, selectTicks, selectTicksReady } from '../../redux/reducers/marketReducer';

import {
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  VpnKey,
  CheckCircle,
  Add,
  Delete,
  Edit,
  AccountBalanceWallet,
  Sync
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Components
import Wrapper from '../../components/Wrapper/Wrapper';
import User_currencies from '../../components/User_currencies/User_currencies';
import Footer from '../../components/Footer/Footer';
import Toolbar from '../../components/Toolbar/Toolbar';
import Logout from '../../containers/Login/Logout';
import Loading from '../../components/Loading/Loading';

// Assets
import bitcoin from '../../assets/logo/bitcoin.png';
import ethereum from '../../assets/logo/ethereum.png';
import litecoin from '../../assets/logo/litecoin.png';
import ripple from '../../assets/logo/Ripple.png';
import EOS from '../../assets/logo/eos.png';
import BNB from '../../assets/logo/bnb.png';
import doge from '../../assets/logo/doge.png';
import ada from '../../assets/logo/ada.png';
import binanceIcon from '../../assets/logo/binance.png';
import coinbaseIcon from '../../assets/logo/coinbase.png';
import krakenIcon from '../../assets/logo/kraken.png';

import exchangeApiService from '../../services/exchangeApiService';
import assetApiService from '../../services/assetApiService';
import useAuth from '../../hooks/useAuth';
import { AssetService } from '../../services/assetService';
import symbolService from '../../services/symbolService';

import { getCryptoPrice } from "../../redux/actions/ProfileAction";

// Styles
import 'react-toastify/dist/ReactToastify.min.css';

const Portfolio = () => {
  const lastGoodPriceRef = useRef(Object.create(null));
  const value = useSelector(state => state.profiledata.value, shallowEqual);
  const isLoadedPrice = useSelector(state => true, shallowEqual);

  const theme = useTheme();
  const dispatch = useDispatch();

  // Exchange Data State
  const [exchanges, setExchanges] = useState([]);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [isLoadingExchanges, setIsLoadingExchanges] = useState(false);
  const [assets, setAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const { userId, isAuthenticated } = useAuth();

  const [liveWallet, setLiveWallet] = useState([]);

  const [connectionType, setConnectionType] = useState('real');
  const [apiCredentials, setApiCredentials] = useState({
    name: '',
    key: '',
    secret: '',
    exchange: 'binance.com',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Simulated Portfolio State
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newHolding, setNewHolding] = useState({
    currency: 'BTC',
    amount: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [symbolsByExchange, setSymbolsByExchange] = useState({});
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState(null);
  const [symbolQuery, setSymbolQuery] = useState('');
  const [assetsLocked, setAssetsLocked] = useState(false);
  const [showEditExchange, setShowEditExchange] = useState(false);

  const [lockSnapshot, setLockSnapshot] = useState(null);
  const [viewMode, setViewMode] = useState('value');
  const [editExchangeId, setEditExchangeId] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
const [futuresAccount, setFuturesAccount] = useState(null);      // { totalMarginBalance, availableBalance, totalUnrealizedProfit, positions: [] }
const [futuresLoading, setFuturesLoading] = useState(false);
const [futuresRefetchKey, setFuturesRefetchKey] = useState(0);   // ← NEW: bumped to re-trigger futures fetch
const [futuresError, setFuturesError] = useState(null);          // ← NEW: surface errors to user
const [futuresFunding, setFuturesFunding] = useState({});         // { [symbol]: { rate, nextTime } }
  const ticks = useSelector(selectTicks, shallowEqual);
  const ticksReady = useSelector(selectTicksReady);
  const STABLES = new Set(['USDT', 'USD', 'USDC', 'BUSD', 'TUSD', 'DAI']);
  const BRIDGE_BASES = ['BTC', 'ETH'];

  // User Data State
  const [userData, setUserData] = useState({
    funds: [],
  });

  // UI State
  const [uiState, setUiState] = useState({
    error: false,
    errorInfo: '',
  });

  // Available cryptocurrencies for simulation
  const cryptoOptions = [
    { symbol: 'BTC', name: 'Bitcoin', icon: bitcoin, precision: 6 },
    { symbol: 'ETH', name: 'Ethereum', icon: ethereum, precision: 4 },
    { symbol: 'LTC', name: 'Litecoin', icon: litecoin, precision: 4 },
    { symbol: 'XRP', name: 'Ripple', icon: ripple, precision: 2 },
    { symbol: 'EOS', name: 'EOS', icon: EOS, precision: 4 },
    { symbol: 'BNB', name: 'Binance Coin', icon: BNB, precision: 4 },
    { symbol: 'DOGE', name: 'Dogecoin', icon: doge, precision: 2 },
    { symbol: 'ADA', name: 'Cardano', icon: ada, precision: 4 },
  ];

  // Available exchanges
  const availableExchanges = [
    { id: 'binance.com', name: 'Binance', icon: binanceIcon },
    { id: 'binance.us', name: 'Binance US', icon: binanceIcon },
  ];

  const POPULAR_BASES = [
    'BTC', 'ETH', 'USDT', 'BNB', 'XRP', 'SOL', 'USDC', 'ADA', 'DOGE', 'TRX',
    'TON', 'DOT', 'AVAX', 'LINK', 'LTC', 'BCH', 'XLM', 'ATOM', 'ETC', 'FIL',
  ];

  const LOGOS = {
    binance: binanceIcon,
    binanceus: binanceIcon,
    coinbase: coinbaseIcon,
    kraken: krakenIcon,
  };

  // Socket (single shared connection)
  const socketRef = useRef(null);

  useEffect(() => {
    if (socketRef.current) return;
    socketRef.current = io(
      process.env.REACT_APP_SOCKET_URL ?? 'http://localhost:3005',
      {
        path: process.env.REACT_APP_SOCKET_IO_PATH || '/socket.io',
        transports: ['websocket', 'polling'],
        autoConnect: true,
      }
    );
    return () => {
      try { socketRef.current.disconnect(); } catch { }
      socketRef.current = null;
    };
  }, []);

  // WebSocket for price ticks
useEffect(() => {
  const wsUrl = process.env.REACT_APP_WEBSERVER_URL || 'wss://api.cryptologics-ai.com/ws/mini';
  
  console.log('[WS] Connecting to:', wsUrl);
  
  let ws = null;
  let reconnectTimeout = null;
  let reconnectDelay = 1000;

  const connect = () => {
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[WS] Connected');
      reconnectDelay = 1000;
    };

    ws.onmessage = (ev) => {
      try {
        const arr = JSON.parse(ev.data);
        if (!Array.isArray(arr)) return;
        dispatch(ticksBatchReceived(arr));
      } catch (e) {
        console.warn('[WS] Parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('[WS] Disconnected, reconnecting in', reconnectDelay, 'ms');
      reconnectTimeout = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 1.5, 30000);
        connect();
      }, reconnectDelay);
    };

    ws.onerror = (err) => {
      console.error('[WS] Error:', err);
      ws.close();
    };
  };

  connect();

  return () => {
    if (reconnectTimeout) clearTimeout(reconnectTimeout);
    if (ws) {
      try { ws.close(); } catch {}
    }
  };
}, [dispatch]);


  // "ADAUSDT" → { base: "ADA", quote: "USDT" }
  function splitSymbol(raw) {
    const s = String(raw || '').toUpperCase();
    const quotes = ['USDT', 'USDC', 'BUSD', 'TUSD', 'USD', 'DAI', 'BTC', 'ETH'];
    quotes.sort((a, b) => b.length - a.length);
    for (const q of quotes) {
      if (s.endsWith(q)) {
        const base = s.slice(0, -q.length);
        if (base) return { base, quote: q };
      }
    }
    return { base: s, quote: '' };
  }

  const getPrecisionForSymbol = (sym) => {
    const base = String(sym || '').toUpperCase();
    const exId = selectedExchange?.id;

    const live = exId ? symbolsByExchange[exId]?.map?.[base] : undefined;
    if (Number.isInteger(live) && live >= 0) return live;

    const fallback = cryptoOptions.find(c => c.symbol === base)?.precision;
    if (Number.isInteger(fallback) && fallback >= 0) return fallback;

    return 8;
  };

  const normalizeWallet = (resp) => {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.wallet)) return resp.wallet;
    return [];
  };

  const normalizeExchangeRow = (row, fallbackName) => {
  if (!row) return null;

  const exchangeKey = row.exchange ?? row.exchangeType ?? "";
  const isSimulated = row.simulated === true;

  // Determine key presence
  const hasApiKey = row.has_api_key ?? !!(row.API_key || row.apiKey);
  const hasSecretKey = row.has_secret_key ?? !!(row.SECRET_key || row.apiSecret);

  return {
    ...row,
    exchangeType: row.exchangeType ?? exchangeKey,
    name: row.name ?? fallbackName ?? exchangeKey ?? "Exchange",
    simulated: isSimulated,
    assets_locked: row.assets_locked === true,
    has_api_key: hasApiKey,
    has_secret_key: hasSecretKey,

    // Keep encrypted keys for live exchanges (needed for API calls)
    // These are ENCRYPTED, not raw keys
    API_key: isSimulated ? undefined : (row.API_key ?? row.apiKey ?? ""),
    SECRET_key: isSimulated ? undefined : (row.SECRET_key ?? row.apiSecret ?? ""),
  };
};


// ── Fetch Binance futures account + funding when a live exchange is selected ──
useEffect(() => {
  if (!selectedExchange || selectedExchange.simulated) {
    setFuturesAccount(null);
    setFuturesFunding({});
    setFuturesError(null);
    return;
  }
 
  const exType = (selectedExchange.exchange ?? selectedExchange.exchangeType ?? '').toLowerCase();
  if (!exType.includes('binance') || exType === 'binance.us' || exType === 'binanceus') {
    // FIX 5: Use exact match instead of .includes('us') which could false-positive
    setFuturesAccount(null);
    setFuturesError(null);
    return;
  }
 
  const controller = new AbortController();
 
  const fetchFutures = async () => {
    setFuturesLoading(true);
    setFuturesError(null);
    try {
      // FIX 1+2: Pass email AND exchange_id so backend can look up keys server-side.
      // Don't rely on selectedExchange.API_key — the list endpoint may not return
      // actual encrypted key values (only has_api_key boolean flag).
      const resp = await AssetService.fetchFuturesAccount({
        exchange_id: selectedExchange.id,                          // ← primary auth: let backend look up keys
        exchange: selectedExchange.exchange ?? selectedExchange.exchangeType,
        email: localStorage.getItem('email'),                      // ← FIX 1: was missing
        // Still pass keys if available (backend can use them as fallback)
        API_key: selectedExchange.API_key || undefined,
        SECRET_key: selectedExchange.SECRET_key || undefined,
      });
 
      const data = resp.data ?? resp;
 
      // Normalize — Binance returns `positions` array with all symbols, filter to non-zero
      const allPositions = Array.isArray(data?.positions) ? data.positions : [];
      const openPositions = allPositions.filter(p => {
        const amt = Math.abs(Number(p.positionAmt ?? p.notional ?? 0));
        return amt > 0;
      });
 
      setFuturesAccount({
        totalMarginBalance: Number(data.totalMarginBalance) || 0,
        availableBalance: Number(data.availableBalance) || 0,
        totalUnrealizedProfit: Number(data.totalUnrealizedProfit) || 0,
        totalWalletBalance: Number(data.totalWalletBalance) || 0,
        totalCrossWalletBalance: Number(data.totalCrossWalletBalance) || 0,
        positions: openPositions.map(p => ({
          symbol: p.symbol,
          side: Number(p.positionAmt) >= 0 ? 'LONG' : 'SHORT',
          positionAmt: Math.abs(Number(p.positionAmt)),
          entryPrice: Number(p.entryPrice),
          markPrice: Number(p.markPrice),
          unrealizedProfit: Number(p.unrealizedProfit),
          leverage: Number(p.leverage),
          marginType: p.marginType,
          liquidationPrice: Number(p.liquidationPrice),
          notional: Math.abs(Number(p.notional || 0)),
          isolatedMargin: Number(p.isolatedMargin || 0),
        })),
      });
 
      // Fetch funding rates for open position symbols (public API, no auth needed)
      if (openPositions.length > 0) {
        const fundingUpdates = {};
        const symbols = [...new Set(openPositions.map(p => p.symbol))];
 
        await Promise.all(symbols.map(async (sym) => {
          try {
            const fRes = await fetch(
              `https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${sym}`,
              { signal: controller.signal }
            );
            const fData = await fRes.json();
            const d = Array.isArray(fData) ? fData[0] : fData;
            fundingUpdates[sym] = {
              rate: Number(d?.lastFundingRate) || 0,
              nextTime: Number(d?.nextFundingTime) || 0,
              markPrice: Number(d?.markPrice) || 0,
              indexPrice: Number(d?.indexPrice) || 0,
            };
          } catch { /* ignore per-symbol failures */ }
        }));
 
        setFuturesFunding(fundingUpdates);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to load futures account';
        console.warn('Futures account fetch failed:', errorMsg);
 
        // FIX 6: Surface meaningful errors to the user instead of silently swallowing
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setFuturesError('API key permissions insufficient for futures. Ensure your key has USDT-M Futures read access.');
        } else if (status === 400) {
          setFuturesError('Futures account not enabled. Enable USDT-M futures on Binance first.');
        } else if (status === 404) {
          setFuturesError('Futures endpoint not available. Check backend configuration.');
        } else {
          // Don't show error for network issues on the 30s polling interval
          // Only show on the initial fetch (futuresRefetchKey helps distinguish)
          if (!futuresAccount) {
            setFuturesError(errorMsg);
          }
        }
      }
    } finally {
      setFuturesLoading(false);
    }
  };
 
  fetchFutures();
  const interval = setInterval(fetchFutures, 30_000);
 
  return () => {
    controller.abort();
    clearInterval(interval);
  };
// FIX 3+4: Added futuresRefetchKey to deps so refresh/key-update re-triggers the fetch
}, [selectedExchange?.id, selectedExchange?.simulated, futuresRefetchKey]);
 

  const getExchangeDisplayName = (ex) => {
    const raw = ex?.exchangeType ?? ex?.exchange ?? ex?.name ?? '';
    const key = normalizeExchangeKey(raw);
    switch (key) {
      case 'binance': return 'Binance';
      case 'binanceus': return 'Binance US';
      case 'coinbase': return 'Coinbase';
      case 'kraken': return 'Kraken';
      default: return raw || 'Exchange';
    }
  };

  const normalizeExchange = (row) => normalizeExchangeRow(row, getExchangeDisplayName(row));

  const normalizeExchangeKey = (s) => {
    const raw = String(s || '').toLowerCase();
    if (raw.includes('binance')) {
      return raw.includes('us') ? 'binanceus' : 'binance';
    }
    if (raw.includes('coinbase')) return 'coinbase';
    if (raw.includes('kraken')) return 'kraken';
    return raw.replace(/[^a-z0-9]/g, '');
  };

  const getExchangeLogo = (ex) => {
    const raw = ex?.exchangeType ?? ex?.exchange ?? ex?.name ?? '';
    const key = normalizeExchangeKey(raw);
    return LOGOS[key];
  };

  const openEditForExchange = (exchange) => {
    const ex = normalizeExchange(exchange);

    setSelectedExchange(ex);
    setEditExchangeId(ex.id);
    setConnectionType(ex.simulated ? "simulated" : "real");
    setAssetsLocked(!!(ex.simulated && ex.assets_locked));

    setApiCredentials({
      name: ex.name || getExchangeDisplayName(ex),
      key: '',
      secret: '',
      exchange: ex.exchange || ex.exchangeType || "binance.com",
    });

    setShowEditExchange(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  function rememberPrice(base, px) {
    if (Number.isFinite(px) && px > 0) lastGoodPriceRef.current[base] = px;
    return lastGoodPriceRef.current[base] || 0;
  }

  function getUSDTPrice(baseSymbol) {
    const base = String(baseSymbol || '').toUpperCase();

    if (STABLES.has(base)) return 1;

    const direct = ticks[base]?.USDT;
    if (Number.isFinite(direct) && direct > 0) {
      return rememberPrice(base, direct);
    }

    for (const q of Object.keys(ticks[base] || {})) {
      if (STABLES.has(q)) {
        const p = ticks[base][q];
        if (Number.isFinite(p) && p > 0) return rememberPrice(base, p);
      }
    }

    for (const bridge of BRIDGE_BASES) {
      const baseVsBridge = ticks[base]?.[bridge];
      const bridgeVsUSDT = ticks[bridge]?.USDT
        || (Object.keys(ticks[bridge] || {}).find(q => STABLES.has(q)) && ticks[bridge][Object.keys(ticks[bridge]).find(q => STABLES.has(q))]);
      if (Number.isFinite(baseVsBridge) && Number.isFinite(bridgeVsUSDT)) {
        const px = baseVsBridge * bridgeVsUSDT;
        if (px > 0) return rememberPrice(base, px);
      }
    }

    const reduxDirect = value?.[`${base}USDT`]?.price;
    if (Number.isFinite(reduxDirect))
      return rememberPrice(base, reduxDirect);
    for (const q of ['USD', 'USDC', 'BUSD', 'TUSD']) {
      const p = value?.[`${base}${q}`]?.price;
      if (Number.isFinite(p) && p > 0) return rememberPrice(base, p);
    }

    return lastGoodPriceRef.current[base] || 0;
  }

  const makeStepFromPrecision = (p) => (p <= 0 ? '1' : `0.${'0'.repeat(p - 1)}1`);

  function unwrapExchange(resp) {
    return resp && resp.exchange ? resp.exchange : resp;
  }

  function coerceLockSnapshotShape(snap) {
    if (!snap || typeof snap !== 'object') return null;
    if (typeof snap.per === 'number' && snap.breakdown && typeof snap.breakdown === 'object') {
      const { breakdown, ...rest } = snap;
      return { ...rest, per: breakdown };
    }
    return snap;
  }

  async function fetchFallbackPrices(bases = []) {
    const want = new Set(bases.map(s => String(s).toUpperCase()));
    const out = Object.create(null);

    try {
      const url = `${process.env.REACT_APP_API_BASEURL}/api/crypto_price`;
      const resp = await axios.post(url, {}, {
        headers: { Authorization: localStorage.getItem('token') }
      });

      const rows = Array.isArray(resp.data) ? resp.data : [];
      const priority = (q) => (q === 'USDT' ? 3 : q === 'USD' ? 2 : STABLES.has(q) ? 1 : 0);

      for (const r of rows) {
        const clsRaw = r?.Symbol?.class_name || r?.class_name || '';
        if (!clsRaw) continue;

        const cls = stripTrailingDigits(clsRaw);
        const { base, quote } = splitSymbol(cls);
        if (!base || !quote) continue;

        const q = String(quote).toUpperCase();
        if (!(STABLES.has(q) || q === 'USD')) continue;

        const px = Number(r?.symbol_price ?? r?.last_price ?? r?.price);
        if (!Number.isFinite(px) || px <= 0) continue;

        const cur = out[base];
        if (!cur || priority(q) > cur.priority) {
          out[base] = { price: px, priority: priority(q) };
        }
      }

      const result = {};
      for (const b of want) result[b] = out[b]?.price || 0;
      return result;
    } catch (e) {
      console.warn('fetchFallbackPrices error:', e?.message || e);
      return {};
    }
  }

  const stripTrailingDigits = (s) => String(s || '').replace(/\d+$/g, '');

  const lockAssets = async () => {
    if (!selectedExchange?.simulated) {
      toast.info("Asset locking is only available for simulated exchanges.");
      return;
    }

    try {
      const userId = parseInt(localStorage.getItem('userId'), 10);
      if (!Number.isFinite(userId)) {
        toast.error("Not signed in");
        return;
      }

      const missing = [];
      for (const a of assets) {
        const sym = String(a.symbol ?? a.currency ?? '').toUpperCase();
        const p = getUSDTPrice(sym);
        if (!Number.isFinite(p) || p <= 0) missing.push(sym);
      }

      let fallback = {};
      if (missing.length) {
        fallback = await fetchFallbackPrices(missing);
      }

      const resolvePrice = (sym) => {
        const live = getUSDTPrice(sym);
        if (Number.isFinite(live) && live > 0) return live;
        const fb = Number(fallback?.[sym]);
        return Number.isFinite(fb) && fb > 0 ? fb : 0;
      };

      const per = {};
      let total = 0;
      for (const a of assets) {
        const sym = String(a.symbol ?? a.currency ?? '').toUpperCase();
        const amount = Number(a.amount) || 0;
        const price = resolvePrice(sym);
        if (!Number.isFinite(price) || price <= 0) {
          toast.error(`No price available for ${sym}. Cannot lock.`);
          return;
        }
        const value = amount * price;
        per[sym] = { amount, price, value };
        total += value;
      }
      const snapshot = { ts: Date.now(), total, per };

      const payload = {
        user_id: userId,
        assets_locked: true,
        lock_ts: snapshot.ts,
        lock_snapshot: snapshot,
      };

      const resp = await exchangeApiService.lock(selectedExchange.id, payload);
      const ex = unwrapExchange(resp);

      if (!ex || !ex.id) {
        toast.error(resp?.message || "Failed to lock assets (bad response)");
        return;
      }
      if (ex.simulated !== true) {
        toast.error("Only simulated exchanges can be locked");
        return;
      }

      const snap = coerceLockSnapshotShape(ex.lock_snapshot);
      setLockSnapshot(snap);
      setAssetsLocked(ex.assets_locked === true);

      setSelectedExchange(ex);
      setExchanges(prev => prev.map(e => (e.id === ex.id ? ex : e)));

      if (snap) localStorage.setItem(`sim-lock:${ex.id}`, JSON.stringify(snap));
      toast.success("Assets locked at current prices.");
    } catch (e) {
      console.error("lockAssets error:", e);
      const msg = e?.response?.data?.message || e?.message || "Failed to lock assets";
      toast.error(msg);
    }
  };

  const walletRows = useMemo(() => {
    const list = Array.isArray(liveWallet) ? liveWallet : [];

    const rows = list
      .map((w) => {
        const asset = String(w.asset || "").toUpperCase();
        const free = Number(w.free || 0);
        const locked = Number(w.locked || 0);
        const total = free + locked;
        if (!asset || total <= 0) return null;

        const px = asset === "USDT" ? 1 : Number(getUSDTPrice(asset) || 0);
        const valueUSDT = px > 0 ? total * px : 0;

        return { asset, free, locked, total, px, valueUSDT };
      })
      .filter(Boolean)
      .sort((a, b) => (b.valueUSDT || 0) - (a.valueUSDT || 0));

    const totalUSDT = rows.reduce((s, r) => s + (r.valueUSDT || 0), 0);

    return { rows, totalUSDT };
  }, [liveWallet, ticks, value]);

  const getStaticChoices = () => buildChoices(cryptoOptions.map(c => c.symbol));

  function makeOptionsFromBases(bases, getPrecisionForSymbol, cryptoOptions) {
    return bases.map(sym => {
      const meta = cryptoOptions.find(c => c.symbol === sym);
      return {
        symbol: sym,
        name: meta?.name || sym,
        icon: meta?.icon || null,
        precision: getPrecisionForSymbol(sym),
      };
    });
  }

  function getTop20ChoicesFor(bases, getPrecisionForSymbol, cryptoOptions) {
    const setAll = new Set(bases);
    const topBases = [];
    for (const s of POPULAR_BASES) {
      if (setAll.has(s)) topBases.push(s);
      if (topBases.length >= 20) break;
    }
    return makeOptionsFromBases(topBases, getPrecisionForSymbol, cryptoOptions);
  }

  function getAllChoicesFor(bases, getPrecisionForSymbol, cryptoOptions) {
    const all = makeOptionsFromBases(bases, getPrecisionForSymbol, cryptoOptions);
    return all.sort((a, b) => a.symbol.localeCompare(b.symbol));
  }

  const buildChoices = (bases) => {
    const all = bases.map(sym => {
      const meta = cryptoOptions.find(c => c.symbol === sym);
      return {
        symbol: sym,
        name: meta?.name || sym,
        icon: meta?.icon || null,
        precision: getPrecisionForSymbol(sym),
      };
    });

    const topOrdered = [];
    const setAll = new Set(bases);
    for (const s of POPULAR_BASES) {
      if (setAll.has(s)) topOrdered.push(s);
      if (topOrdered.length >= 20) break;
    }

    const topSet = new Set(topOrdered);
    const top = all.filter(x => topSet.has(x.symbol));
    const rest = all.filter(x => !topSet.has(x.symbol)).sort((a, b) => a.symbol.localeCompare(b.symbol));

    return [
      ...top.map(x => ({ ...x, group: 'Top' })),
      ...rest.map(x => ({ ...x, group: 'All' })),
    ];
  };

  const autoFilter = createFilterOptions({
    stringify: (opt) => `${opt.symbol} ${opt.name}`,
  });

  const getSimulatedBaseChoices = () => {
    const exId = selectedExchange?.id;
    const bases = exId ? (symbolsByExchange[exId]?.bases || []) : [];
    return buildChoices(bases);
  };

  // Effects
  useEffect(() => {
    try {
      const raw = localStorage.getItem('last_good_prices');
      if (raw) lastGoodPriceRef.current = JSON.parse(raw) || {};
    } catch { }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('last_good_prices', JSON.stringify(lastGoodPriceRef.current));
    } catch { }
  }, [ticks]);

  useEffect(() => {
    if (!selectedExchange) return;
    const haveAnyPrices =
      ticksReady ||
      (value && Object.keys(value || {}).length > 0) ||
      Object.keys(lastGoodPriceRef.current || {}).length > 0;
    if (!haveAnyPrices) return;
    setUserData({ funds: convertAssetsToFunds(assets) });
  }, [ticks, ticksReady, value, assets, selectedExchange?.id]);

  useEffect(() => {
    const loadExchangeSymbols = async () => {
      if (!selectedExchange?.id || !selectedExchange?.simulated) return;

      setSymbolsLoading(true);
      setSymbolsError(null);
      try {
        const rows = await symbolService.get(
          selectedExchange.exchange ?? selectedExchange.exchangeType,
          'objects'
        );
        const baseSet = new Set();
        const basePrecision = {};

        for (const r of rows || []) {
          const base = String(r.base_asset ?? '')
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '');
          if (!base) continue;

          baseSet.add(base);

          const p = Number.isInteger(r?.precision) ? Number(r.precision) : null;
          if (p != null && p >= 0) {
            basePrecision[base] = Math.max(p, basePrecision[base] ?? 0);
          }
        }

        setSymbolsByExchange(prev => ({
          ...prev,
          [selectedExchange.id]: {
            bases: Array.from(baseSet).sort(),
            map: basePrecision,
            updatedAt: Date.now(),
          },
        }));
      } catch (e) {
        setSymbolsError(e?.message || 'Failed to load symbols/precisions');
        console.warn('Symbol load error:', e);
      } finally {
        setSymbolsLoading(false);
      }
    };

    loadExchangeSymbols();
  }, [selectedExchange?.id, selectedExchange?.simulated]);

  useEffect(() => {
    if (!selectedExchange?.id || !selectedExchange?.simulated) {
      setLockSnapshot(null);
      return;
    }
    const serverSnap = coerceLockSnapshotShape(selectedExchange.lock_snapshot);
    if (serverSnap?.per) {
      setLockSnapshot(serverSnap);
      return;
    }
    try {
      const raw = localStorage.getItem(`sim-lock:${selectedExchange.id}`);
      const snap = raw ? coerceLockSnapshotShape(JSON.parse(raw)) : null;
      setLockSnapshot(snap && snap.per ? snap : null);
    } catch {
      setLockSnapshot(null);
    }
  }, [selectedExchange?.id, selectedExchange?.simulated]);

  useEffect(() => {
    if (!selectedExchange?.id || !selectedExchange?.simulated) return;
    const exId = String(selectedExchange.id);

    socketRef.current?.emit('join-sim-ex', exId);

    const onSimPortfolio = (payload) => {
      applySimPortfolioSnapshot(payload);
    };
    const onFill = (evt) => {
      const side = evt?.side || 'TRADE';
      const sym = (evt?.symbol || '').toUpperCase();
      const px = evt?.price;
      const qty = evt?.quantity ?? evt?.qty;
      toast.info(`${side} ${qty ?? ''} ${sym} @ ${px ?? ''}`.trim());
    };
    const onLocked = ({ assets_locked }) => {
      const locked = !!assets_locked;
      setAssetsLocked(locked);
      setSelectedExchange((prev) => prev ? { ...prev, assets_locked: locked } : prev);
      setExchanges((prev) => prev.map(ex => ex.id === selectedExchange.id ? { ...ex, assets_locked: locked } : ex));
    };

    socketRef.current?.on('sim-portfolio', onSimPortfolio);
    socketRef.current?.on('fill', onFill);
    socketRef.current?.on('assets-locked', onLocked);
    socketRef.current?.on('sim-delta', onSimPortfolio);

    return () => {
      socketRef.current?.off('sim-portfolio', onSimPortfolio);
      socketRef.current?.off('fill', onFill);
      socketRef.current?.off('assets-locked', onLocked);
      socketRef.current?.off('sim-delta', onSimPortfolio);
      socketRef.current?.emit('leave-sim-ex', exId);
    };
  }, [selectedExchange?.id, selectedExchange?.simulated]);

  useEffect(() => {
    fetchExchanges();
  }, []);

  useEffect(() => {
    if (selectedExchange?.simulated) {
      fetchAssets(selectedExchange.id);
    }
  }, [selectedExchange?.id]);

  useEffect(() => {
    fetchCryptoPrices();
    fetchUserExchanges();
  }, []);

  // Apply a full snapshot payload from socket to local state
  const applySimPortfolioSnapshot = (payload = {}) => {
    const list = Array.isArray(payload) ? payload : (payload.assets || []);
    if (!Array.isArray(list)) return;
    setAssets(list);
    setUserData({ funds: convertAssetsToFunds(list) });
  };

  // Data Fetching Functions
  const fetchUserExchanges = useCallback(async () => {
    setIsLoadingExchanges(true);
    try {
      const userId = parseInt(localStorage.getItem("userId"), 10);
      if (!userId) throw new Error("User ID not found - please login again");

      const exchangesData = await exchangeApiService.list();
      const normalized = (exchangesData || []).map(normalizeExchange);
      setExchanges(normalized);

      if (selectedExchange?.id) {
        const latest = normalized.find((e) => String(e.id) === String(selectedExchange.id));
        if (latest) {
          setSelectedExchange(latest);
          setAssetsLocked(!!(latest.simulated && latest.assets_locked));
        }
      }
    } catch (error) {
      console.error("Failed to fetch exchanges:", error);
      toast.error(error.message || "Failed to load exchanges");

      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        window.location.href = "/login";
      }
    } finally {
      setIsLoadingExchanges(false);
    }
  }, [selectedExchange?.id]);

  const fetchExchanges = async () => {
    setIsLoadingExchanges(true);
    try {
      const exchangesData = await exchangeApiService.list();
      setExchanges(exchangesData);
      if (selectedExchange) {
        const latest = (exchangesData || []).find(e => e.id === selectedExchange.id);
        if (latest) {
          setSelectedExchange(latest);
          setAssetsLocked(!!(latest.simulated && latest.assets_locked));
        }
      }
    } catch (error) {
      toast.error("Failed to fetch exchanges");
      console.error("Exchange fetch error:", error);
    } finally {
      setIsLoadingExchanges(false);
    }
  };

  const fetchAssets = async (exchangeId) => {
    setIsLoadingAssets(true);
    try {
      const assetsData = await assetApiService.list(exchangeId);
      setAssets(assetsData);
      setUserData({ funds: convertAssetsToFunds(assetsData) });
    } catch (error) {
      console.error('Asset fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to load assets');

      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        window.location.href = '/login';
      }
    } finally {
      setIsLoadingAssets(false);
    }
  };

  function savePricePayloadToStorage(payload) {
    try {
      localStorage.setItem('price_payload', JSON.stringify(payload));
      localStorage.setItem('price_call_time', String(moment.utc().valueOf()));
    } catch { }
  }

  function loadPricePayloadFromStorage() {
    try {
      const raw = localStorage.getItem('price_payload');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  const fetchCryptoPrices = useCallback(() => {
    const lastTs = Number(localStorage.getItem('price_call_time') || 0);
    const staleCutoff = moment.utc().valueOf() - 30 * 60 * 1000;
    const reduxHasData = !!value && Object.keys(value || {}).length > 0;
    const stored = loadPricePayloadFromStorage();

    if (!reduxHasData && stored) {
      dispatch(getCryptoPrice(stored));
    }

    const shouldFetch = (lastTs < staleCutoff) || (!reduxHasData && !stored);
    if (!shouldFetch) return;

    const url = `${process.env.REACT_APP_API_BASEURL}/api/crypto_price`;
    axios.post(url, {}, { headers: { 'Authorization': localStorage.getItem('token') } })
      .then(response => {
        const payload = response.data?.[0] || {};
        dispatch(getCryptoPrice(payload));
        savePricePayloadToStorage(payload);
      })
      .catch(error => console.error('Price data error:', error));
  }, [dispatch, value]);

  // Handlers
  const handleApiKeyChange = (event) => {
    setApiCredentials(prev => ({ ...prev, key: event.target.value }));
  };

  const handleSecretChange = (event) => {
    setApiCredentials(prev => ({ ...prev, secret: event.target.value }));
  };

  const handleExchangeChange = (exchangeKey) => {
    setApiCredentials(prev => ({
      ...prev,
      exchange: exchangeKey
    }));
  };

  const handleNameChange = (event) => {
    setApiCredentials(prev => ({ ...prev, name: event.target.value }));
  };

  const handleConnectionTypeChange = (event) => {
    setConnectionType(event.target.value);
  };

  const handleSelectExchange = async (exchange) => {
  const ex = normalizeExchange(exchange);

  setSelectedExchange(ex);
  openEditForExchange(ex);
  setAssetsLocked(!!(ex.simulated && ex.assets_locked));

  setIsLoadingAssets(true);
  try {
    if (ex.simulated) {
      setLiveWallet([]);
      const assetsData = await assetApiService.list(ex.id);
      setAssets(assetsData);
      setUserData({ funds: convertAssetsToFunds(assetsData) });
    } else {
      // Live exchange - fetch wallet with encrypted keys
      const resp = await AssetService.fetchRealAssets({
        API_key: ex.API_key,
        SECRET_key: ex.SECRET_key,
        exchange: ex.exchange ?? ex.exchangeType,
        exchange_id: ex.id,        // ✅ sent to backend  
        email: localStorage.getItem('email'),
      });
      console.log('resp live', resp)
      
      const wallet = Array.isArray(resp) 
        ? resp 
        : (Array.isArray(resp?.wallet) ? resp.wallet : []);
      
      setLiveWallet(wallet);
      setAssets([]);
      setUserData({ funds: wallet });
    }
  } catch (error) {
    console.error("handleSelectExchange error:", error);
    toast.error(error.response?.data?.message || "Failed to load assets");
  } finally {
    setIsLoadingAssets(false);
  }
};

 const refreshAssets = async () => {
  if (!selectedExchange) return;
 
  setIsLoadingAssets(true);
  try {
    if (!selectedExchange.simulated) {
      AssetService.clearCache(selectedExchange.id);
      AssetService.clearFuturesCache(selectedExchange.id);
      // FIX 3: Bump refetch key to re-trigger the futures useEffect
      setFuturesRefetchKey(k => k + 1);
    }
    await handleSelectExchange(selectedExchange);
    toast.success("Assets refreshed");
  } catch (error) {
    toast.error("Failed to refresh assets");
  } finally {
    setIsLoadingAssets(false);
  }
};

  const handleAddHolding = async () => {
    if (assetsLocked) {
      toast.error("Assets are locked. You cannot add new holdings.");
      return;
    }
    const raw = String(newHolding.amount).replace(',', '.').trim();
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num <= 0) {
      toast.error("Amount must be a positive number");
      return;
    }
    const targetSym = String(newHolding.currency).toUpperCase();

    if (selectedExchange?.simulated) {
      const exId = selectedExchange.id;
      const allowed = new Set(symbolsByExchange[exId]?.bases || []);
      if (!allowed.has(targetSym)) {
        toast.error(`"${targetSym}" is not available on this exchange`);
        return;
      }
    }

    const existingAsset = assets.find(
      a => String(a.symbol ?? a.currency).toUpperCase() === targetSym
    );
    if (existingAsset) {
      toast.error(`You already have ${targetSym} in this exchange`);
      return;
    }

    try {
      const precision = getPrecisionForSymbol(targetSym);
      const roundedAmount = parseFloat(num.toFixed(precision));

      const created = await assetApiService.create({
        exchange_id: selectedExchange.id,
        symbol: targetSym,
        amount: roundedAmount,
      });

      const newAsset = { id: created?.id, symbol: targetSym, amount: roundedAmount };
      const updated = [...assets, newAsset];
      setAssets(updated);
      setUserData({ funds: convertAssetsToFunds(updated) });

      toast.success(`${targetSym} added to portfolio`);
      setIsAddDialogOpen(false);
      setNewHolding({ currency: targetSym, amount: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add asset");
    }
  };

  const handleRemoveHolding = async (id) => {
    if (assetsLocked) {
      toast.error("Assets are locked. You cannot remove holdings.");
      return;
    }
    try {
      await assetApiService.delete(id);
      const updatedAssets = assets.filter(asset => asset.id !== id);
      setAssets(updatedAssets);
      setUserData({ funds: convertAssetsToFunds(updatedAssets) });
      setDeleteConfirm(null);
      toast.success("Asset removed successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove asset");
    }
  };

  const handleDeleteExchange = async () => {
    if (!selectedExchange) return;

    try {
      await exchangeApiService.delete(selectedExchange.id);
      setExchanges(exchanges.filter(ex => ex.id !== selectedExchange.id));
      setSelectedExchange(null);
      resetConnectionForm();
      toast.success("Exchange deleted successfully");
    } catch (error) {
      toast.error("Failed to delete exchange");
      console.error("Delete exchange error:", error);
    }
  };

  const validateApiKeys = () => {
    if (connectionType === 'simulated') return true;

    if (!apiCredentials.name) {
      toast.error("Please enter a name for this connection");
      return false;
    }

    if (apiCredentials.key.length < 20) {
      toast.error("API Key appears invalid");
      return false;
    }
    if (apiCredentials.secret.length < 30) {
      toast.error("Secret Key appears invalid");
      return false;
    }
    return true;
  };

  const validateExchangeUpdate = () => {
    if (!apiCredentials.name) {
      toast.error("Please enter a name for this connection");
      return false;
    }
    return true;
  };

  const connectExchange = async () => {
    if (!validateExchangeUpdate()) return;

    if (selectedExchange) {
      await updateExchange();
    } else {
      if (connectionType === 'real') {
        await connectRealExchange();
      } else {
        await addSimulatedExchange();
      }
    }
  };

  const updateExchange = async () => {
    if (!selectedExchange) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: apiCredentials.name,
        exchange: apiCredentials.exchange,
      };

      if (selectedExchange?.simulated) {
        payload.assets_locked = selectedExchange.assets_locked === true;
      }

      const response = await exchangeApiService.update(selectedExchange.id, payload);
      const updatedExchange = response.exchange || response;

      setExchanges(exchanges.map(ex =>
        ex.id === updatedExchange.id ? {
          ...updatedExchange,
          has_api_key: updatedExchange.has_api_key,
          has_secret_key: updatedExchange.has_secret_key,
        } : ex
      ));

      if (selectedExchange.id === updatedExchange.id) {
        setSelectedExchange({
          ...updatedExchange,
          has_api_key: updatedExchange.has_api_key,
          has_secret_key: updatedExchange.has_secret_key,
        });
      }

      toast.success("Exchange updated successfully");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Update error details:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to update exchange");
    } finally {
      setIsSubmitting(false);
    }
  };

  const connectRealExchange = async () => {
  if (!validateApiKeys()) return;

  setIsSubmitting(true);
  try {
    const exchangeName = apiCredentials.name; // Save before reset
    
    const resp = await exchangeApiService.addExchange({
      email: localStorage.getItem("email"),
      exchange: apiCredentials.exchange,
      apiKey: apiCredentials.key,
      secretKey: apiCredentials.secret,
      name: exchangeName,
      userId: parseInt(localStorage.getItem("userId"), 10),
    });

    const createdRaw = resp?.created;
    
    // Use the name we sent, not what backend returned (in case backend doesn't return it)
    const created = normalizeExchangeRow(createdRaw, exchangeName);
    
    // Ensure name is set
    if (!created.name) {
      created.name = exchangeName || apiCredentials.exchange;
    }

    const wallet = normalizeWallet(resp);

    if (!created) {
      toast.error("Exchange created response missing.");
      return;
    }

    setExchanges((prev) => {
      const withoutDup = prev.filter((e) => String(e.id) !== String(created.id));
      return [...withoutDup, created];
    });

    setSelectedExchange(created);
    setLiveWallet(wallet);
    setAssets([]);
    setUserData({ funds: wallet });

    // Don't call openEditForExchange - set credentials directly to preserve name
    setEditExchangeId(created.id);
    setConnectionType("real");
    setAssetsLocked(false);
    setApiCredentials({
      name: created.name || exchangeName,  // Use the name we know
      key: '',
      secret: '',
      exchange: created.exchange || created.exchangeType || apiCredentials.exchange,
    });
    setShowEditExchange(true);

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);

    toast.success("Live exchange added.");
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      "Failed to add live exchange. Check API/secret/IP/permissions.";
    toast.error(msg);
    console.error("connectRealExchange error:", error?.response?.data || error);
  } finally {
    setIsSubmitting(false);
  }
};

  const addSimulatedExchange = async () => {
    if (!apiCredentials.name?.trim()) {
      toast.error("Please enter a name for this exchange");
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await exchangeApiService.create({
        name: apiCredentials.name,
        exchange: apiCredentials.exchange,
        simulated: true,
        user_id: parseInt(localStorage.getItem("userId"), 10),
        email: localStorage.getItem("email"),
      });

      const created = normalizeExchange(resp);

      if (!created?.id) {
        toast.error("Failed to create simulated exchange");
        return;
      }

      setExchanges(prev => [...prev, created]);
      setSelectedExchange(created);
      openEditForExchange(created);

      setAssets([]);
      setLiveWallet([]);
      setUserData({ funds: [] });
      setAssetsLocked(false);

      resetConnectionInputs();
      toast.success("Simulated exchange created");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to create simulated exchange";
      toast.error(msg);
      console.error("addSimulatedExchange error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetConnectionInputs = () => {
    setApiCredentials({ name: '', key: '', secret: '', exchange: 'binance.com' });
    setShowSecret(false);
    setShowSuccess(false);
    setIsSubmitting(false);
  };

  const resetConnectionForm = () => {
    resetConnectionInputs();
    setSelectedExchange(null);
    setAssets([]);
    setUserData({ funds: [] });
  };

  const confirmDeleteExchange = async () => {
    try {
      await exchangeApiService.delete(selectedExchange.id);
      setExchanges(exchanges.filter(ex => ex.id !== selectedExchange.id));
      setSelectedExchange(null);
      resetConnectionForm();
      toast.success("Exchange deleted successfully");
    } catch (error) {
      toast.error("Failed to delete exchange");
      console.error("Delete exchange error:", error);
    }
  };

  // Dedicated handler for key updates via modal
  const handleUpdateKeys = async () => {
  if (!apiCredentials.key?.trim() || !apiCredentials.secret?.trim()) {
    toast.error("Both API Key and Secret are required");
    return;
  }

  if (apiCredentials.key.length < 20 || apiCredentials.secret.length < 30) {
    toast.error("Keys appear invalid - please check and try again");
    return;
  }

  setIsSubmitting(true);
  try {
    // Use the new dedicated keys endpoint
    const response = await exchangeApiService.updateKeys(selectedExchange.id, {
      API_key: apiCredentials.key,
      SECRET_key: apiCredentials.secret,
    });

    const updatedExchange = response.exchange;

    // Update local state
    setSelectedExchange(prev => ({
      ...prev,
      has_api_key: true,
      has_secret_key: true,
      keys_updated_at: updatedExchange.keys_updated_at,
    }));

    setExchanges(prev => prev.map(ex =>
      ex.id === selectedExchange.id
        ? { ...ex, has_api_key: true, has_secret_key: true }
        : ex
    ));

    // If wallet data was returned, update it
    if (response.wallet) {
      setLiveWallet(response.wallet);
    }

    // Clear form and close modal
    setApiCredentials(prev => ({ ...prev, key: '', secret: '' }));
    setShowKeyModal(false);
    toast.success("API keys updated successfully");
        setFuturesRefetchKey(k => k + 1);


  } catch (error) {
    const message = error.response?.data?.message || "Failed to update keys";
    toast.error(message);
    console.error("handleUpdateKeys error:", error.response?.data || error);
  } finally {
    setIsSubmitting(false);
  }
};

  // Format helpers
  const formatCrypto = (amount, symbol) => {
    const precision = getPrecisionForSymbol(symbol);
    const num = Number(amount);
    return Number.isFinite(num)
      ? num.toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision })
      : (0).toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision });
  };

  const formatUSD = (amount) => {
    const num = Number(amount);
    return Number.isFinite(num)
      ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : (0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPct = (x) =>
    Number.isFinite(x) ? `${x.toFixed(2)}%` : '0.00%';

  const fmtQty = (n, max = 8) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString(undefined, { maximumFractionDigits: max })
      : "—";

  const fmtMoney = (n, max = 2) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString(undefined, { maximumFractionDigits: max })
      : "—";

  const convertAssetsToFunds = (assets = []) => {
    return assets.map(asset => {
      const sym = String(asset.symbol ?? asset.currency ?? '').toUpperCase();
      const price = getUSDTPrice(sym);
      const amount = Number(asset.amount) || 0;

      const cost_usd = Number(asset.usd_amount ?? asset.cost_usd ?? asset.cost ?? 0) || 0;
      const value_usd = amount * price;
      const pnl_usd = value_usd - cost_usd;
      const pnl_pct = cost_usd > 0 ? (pnl_usd / cost_usd) * 100 : 0;

      const lockPer = lockSnapshot?.per?.[sym];
      const baseAtLockValue = Number(lockPer?.value);
      const baseAtLockAmount = Number(lockPer?.amount);

      let sinceLockUsd = 0, sinceLockPct = 0, sinceLockAmount = 0;
      if (Number.isFinite(baseAtLockValue) && baseAtLockValue > 0) {
        sinceLockUsd = value_usd - baseAtLockValue;
        sinceLockPct = (sinceLockUsd / baseAtLockValue) * 100;
      }
      if (Number.isFinite(baseAtLockAmount)) {
        sinceLockAmount = amount - baseAtLockAmount;
      }

      const crypto = cryptoOptions.find(c => c.symbol === sym);

      return {
        id: asset.id,
        symbol: `${sym}USDT`,
        currency: sym,
        name: crypto?.name || sym,
        icon: crypto?.icon,
        amount,
        price,
        value: value_usd,
        cost_usd,
        pnl_usd,
        pnl_pct,
        since_lock_usd: sinceLockUsd,
        since_lock_pct: sinceLockPct,
        since_lock_amount: sinceLockAmount,
      };
    });
  };

  const renderExchangeIcon = (exchange) => {
    const logo = getExchangeLogo(exchange);
    return logo ? (
      <Avatar src={logo} alt={getExchangeDisplayName(exchange)} sx={{ width: 24, height: 24, mr: 1 }} />
    ) : (
      <AccountBalanceWallet sx={{ width: 24, height: 24, mr: 1 }} />
    );
  };

  // Render Functions
  const renderConnectionForm = () => {
    const isEditing = !!selectedExchange;
    const isRealExchange = !selectedExchange?.simulated && connectionType === 'real';

    return (
      <Card elevation={3} sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {isEditing ? `Edit ${selectedExchange.name}` : 'Connect New Exchange'}
          </Typography>

          <TextField
            fullWidth
            label="Exchange Name"
            value={apiCredentials.name}
            onChange={handleNameChange}
            sx={{ mb: 3 }}
            placeholder="e.g., My Binance Account"
          />

          {/* Exchange type selector - only for new */}
          {!isEditing && (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Connection Type</InputLabel>
                <Select
                  value={connectionType}
                  onChange={handleConnectionTypeChange}
                  label="Connection Type"
                >
                  <MenuItem value="real">Real Exchange</MenuItem>
                  <MenuItem value="simulated">Simulated Exchange</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Exchange</InputLabel>
                <Select
                  value={apiCredentials.exchange}
                  onChange={(e) => handleExchangeChange(e.target.value)}
                  label="Exchange"
                >
                  {availableExchanges.map(ex => (
                    <MenuItem key={ex.id} value={ex.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={ex.icon} alt={ex.name} width={20} />
                        {ex.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Key fields ONLY for new real exchanges */}
{connectionType === 'real' && (
  <>
    {process.env.REACT_APP_SERVER_IPS && (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          When creating your Binance API key, whitelist {process.env.REACT_APP_SERVER_IPS.includes(',') ? 'these IPs' : 'this IP'}:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {process.env.REACT_APP_SERVER_IPS.split(',').map((ip, i) => (
            <Chip
              key={i}
              label={ip.trim()}
              size="small"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              onClick={() => {
                navigator.clipboard.writeText(ip.trim());
                toast.success('IP copied!');
              }}
              icon={<VpnKey sx={{ fontSize: 14 }} />}
            />
          ))}
        </Box>
      </Alert>
    )}
    <TextField
      fullWidth
      label="API Key"
                    value={apiCredentials.key}
                    onChange={handleApiKeyChange}
                    sx={{ mb: 2 }}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Secret Key"
                    type={showSecret ? 'text' : 'password'}
                    value={apiCredentials.secret}
                    onChange={handleSecretChange}
                    sx={{ mb: 3 }}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowSecret(!showSecret)}>
                            {showSecret ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </>
          )}

          {/* For editing real exchanges - show key status + manage button */}
          {isEditing && isRealExchange && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2">API Credentials</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip
                      size="small"
                      icon={selectedExchange.has_api_key ? <CheckCircle /> : null}
                      label={selectedExchange.has_api_key ? "Key configured" : "No key"}
                      color={selectedExchange.has_api_key ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={selectedExchange.has_secret_key ? <CheckCircle /> : null}
                      label={selectedExchange.has_secret_key ? "Secret configured" : "No secret"}
                      color={selectedExchange.has_secret_key ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VpnKey />}
                  onClick={() => setShowKeyModal(true)}
                >
                  Manage Keys
                </Button>
              </Box>
            </Box>
          )}

          <Collapse in={isSubmitting}>
            <LinearProgress sx={{ mb: 2 }} />
          </Collapse>

          <Collapse in={showSuccess}>
            <Alert
              icon={<CheckCircle fontSize="inherit" />}
              severity="success"
              sx={{ mb: 2 }}
            >
              {isEditing ? 'Exchange updated successfully!' : 'Exchange connected successfully!'}
            </Alert>
          </Collapse>

          <Button
            fullWidth
            variant="contained"
            onClick={connectExchange}
            disabled={isSubmitting}
            size="large"
          >
            {isSubmitting ? 'Processing...' : isEditing ? 'Save Changes' : 'Connect'}
          </Button>

          {isEditing && (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={resetConnectionForm}
                sx={{ mt: 1 }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth
                variant="outlined"
                color="error"
                onClick={() => setDeleteConfirm({ open: true, type: 'exchange' })}
                disabled={isSubmitting}
                startIcon={<Delete />}
              >
                Delete Exchange
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderKeyModal = () => (
    <Dialog
      open={showKeyModal}
      onClose={() => {
        setShowKeyModal(false);
        setApiCredentials(prev => ({ ...prev, key: '', secret: '' }));
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Update API Keys
        <Typography variant="body2" color="text.secondary">
          {selectedExchange?.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
{process.env.REACT_APP_SERVER_IPS && (
  <Alert severity="info" sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      Ensure {process.env.REACT_APP_SERVER_IPS.includes(',') ? 'these IPs are' : 'this IP is'} whitelisted on your API key:
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {process.env.REACT_APP_SERVER_IPS.split(',').map((ip, i) => (
        <Chip
          key={i}
          label={ip.trim()}
          size="small"
          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
          onClick={() => {
            navigator.clipboard.writeText(ip.trim());
            toast.success('IP copied!');
          }}
          icon={<VpnKey sx={{ fontSize: 14 }} />}
        />
      ))}
    </Box>
  </Alert>
)}

        <TextField
          fullWidth
          label="API Key"
          value={apiCredentials.key}
          onChange={handleApiKeyChange}
          sx={{ mb: 2 }}
          placeholder="Enter new API key"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKey color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          label="Secret Key"
          type={showSecret ? 'text' : 'password'}
          value={apiCredentials.secret}
          onChange={handleSecretChange}
          placeholder="Enter new secret key"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowSecret(!showSecret)}>
                  {showSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          💡 Use read-only API keys without withdrawal permissions for security.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setShowKeyModal(false);
          setApiCredentials(prev => ({ ...prev, key: '', secret: '' }));
        }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateKeys}
          disabled={!apiCredentials.key || !apiCredentials.secret || isSubmitting}
        >
          {isSubmitting ? 'Updating...' : 'Update Keys'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderAddHoldingDialog = () => {
    const isSim = !!selectedExchange?.simulated;

    const curPrecision = getPrecisionForSymbol(newHolding.currency);
    const step = makeStepFromPrecision(curPrecision);

    const exId = selectedExchange?.id;
    const bases = isSim
      ? (symbolsByExchange[exId]?.bases || [])
      : cryptoOptions.map(c => c.symbol);

    const topChoices = getTop20ChoicesFor(bases, getPrecisionForSymbol, cryptoOptions);
    const allChoices = getAllChoicesFor(bases, getPrecisionForSymbol, cryptoOptions);

    const q = symbolQuery.trim().toLowerCase();
    const filteredChoices = q
      ? allChoices.filter(
        o =>
          o.symbol.toLowerCase().includes(q) ||
          (o.name || '').toLowerCase().includes(q)
      )
      : topChoices;

    return (
      <Dialog open={isAddDialogOpen && !assetsLocked} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Add Crypto Holding</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search all symbols"
            placeholder="Type symbol or name (e.g., BTC, Ether)…"
            value={symbolQuery}
            onChange={(e) => setSymbolQuery(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />

          <Autocomplete
            options={filteredChoices}
            loading={symbolsLoading && isSim}
            getOptionLabel={(option) => option.symbol}
            value={
              filteredChoices.find(o => o.symbol === newHolding.currency) ||
              { symbol: newHolding.currency, name: newHolding.currency, precision: getPrecisionForSymbol(newHolding.currency) }
            }
            onChange={(_, opt) => {
              if (opt?.symbol) setNewHolding(prev => ({ ...prev, currency: opt.symbol }));
            }}
            isOptionEqualToValue={(opt, val) => opt.symbol === val.symbol}
            renderOption={(props, option) => (
              <li {...props} key={option.symbol}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {option.icon && <img src={option.icon} alt={option.name} width="20" />}
                  <Typography variant="body2">
                    {option.name} ({option.symbol})
                  </Typography>
                  {assets.some(a => String(a.symbol ?? a.currency).toUpperCase() === option.symbol) && (
                    <Chip size="small" label="Added" sx={{ ml: 1 }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Top 20 (use search above for more)"
                variant="outlined"
                placeholder="Select a symbol"
              />
            )}
          />

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={newHolding.amount}
            onChange={(e) => setNewHolding((prev) => ({ ...prev, amount: e.target.value }))}
            onBlur={(e) => {
              const raw = e.target.value.trim().replace(',', '.');
              setNewHolding((prev) => ({ ...prev, amount: raw }));
            }}
            InputProps={{ inputProps: { step, min: undefined } }}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddHolding} variant="contained" disabled={assetsLocked}>Add</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDeleteConfirm = () => (
    <Dialog
      open={Boolean(deleteConfirm?.open)}
      onClose={() => setDeleteConfirm(null)}
    >
      <DialogTitle>Confirm Removal</DialogTitle>
      <DialogContent>
        {deleteConfirm?.type === 'exchange' ? (
          <Typography>
            Are you sure you want to delete the exchange "{selectedExchange?.name}"?
            <br />
            This will also delete all associated assets and trading bots for this exchange.
          </Typography>
        ) : (
          <Typography>
            Are you sure you want to remove this holding from your {selectedExchange?.name} portfolio?
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
        <Button
          onClick={() => {
            if (deleteConfirm?.type === 'exchange') {
              confirmDeleteExchange();
            } else {
              handleRemoveHolding(deleteConfirm?.id);
            }
            setDeleteConfirm(null);
          }}
          color="error"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderExchangeList = () => {
    if (isLoadingExchanges) {
      return (
        <Box sx={{ width: '100%' }}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Box>
      );
    }

    if (exchanges.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          You haven't connected any exchanges yet. Connect your first exchange above.
        </Alert>
      );
    }

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Exchanges
          </Typography>
          <List>
            {exchanges.map(exchange => {
              return (
                <React.Fragment key={exchange.id}>
                  <ListItem
                    button
                    onClick={() => handleSelectExchange(exchange)}
                    selected={selectedExchange?.id === exchange.id}
                    sx={{
                      borderRadius: 1,
                      backgroundColor: selectedExchange?.id === exchange.id
                        ? theme.palette.action.selected
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <ListItemAvatar>
                      {renderExchangeIcon(exchange)}
                    </ListItemAvatar>
                    <ListItemText
                      primary={exchange.name || getExchangeDisplayName(exchange)}
                      secondary={
                        <>
                          <Box component="span" sx={{ display: 'block' }}>
                            {getExchangeDisplayName(exchange)}
                          </Box>
                          <Box component="span">
                            {exchange.simulated ? (
                              <Chip label="Simulated" size="small" color="info" sx={{ mr: 1 }} />
                            ) : (
                              <>
                                <Chip label="Live" size="small" color="success" sx={{ mr: 1 }} />
                                {exchange.has_api_key && exchange.has_secret_key && (
                                  <Chip
                                    label="Keys Configured"
                                    size="small"
                                    color="default"
                                    icon={<Lock />}
                                  />
                                )}
                              </>
                            )}
                            {exchange.simulated ? 'Test Account' : 'Real Account'}
                          </Box>
                        </>
                      }
                    />
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditForExchange(exchange);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderPortfolioActions = () => {
    if (!selectedExchange) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">
            {selectedExchange.name} Portfolio
          </Typography>
          <Chip
            label={selectedExchange.simulated ? "Simulated" : "Live"}
            size="small"
            color={selectedExchange.simulated ? "info" : "success"}
          />
          {assetsLocked && <Chip label="Locked" size="small" color="warning" />}
        </Box>
        {selectedExchange.simulated && (
          <Box sx={{ display: 'inline-flex', mr: 1 }}>
            <Button
              size="small"
              variant={viewMode === 'value' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('value')}
              sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            >
              Value Δ
            </Button>
            <Button
              size="small"
              variant={viewMode === 'position' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('position')}
              sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            >
              Position Δ
            </Button>
          </Box>
        )}

        <Box>
          {selectedExchange.simulated && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                if (assetsLocked) {
                  toast.error("Assets are locked. You cannot add holdings.");
                  return;
                }
                setIsAddDialogOpen(true);
              }}
              sx={{ mr: 1 }}
              disabled={isLoadingAssets || assetsLocked}
            >
              Add Holding
            </Button>
          )}

          {selectedExchange.simulated && !assetsLocked && (
            <Button
              variant="contained"
              color="warning"
              onClick={lockAssets}
              sx={{ mr: 1 }}
            >
              Lock Assets
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={refreshAssets}
            disabled={isLoadingAssets}
          >
            Refresh
          </Button>
        </Box>
      </Box>
    );
  };

  const renderPortfolioSummary = () => {
    if (!selectedExchange) return null;
    if (!selectedExchange.simulated) return null;

    const funds = Array.isArray(userData.funds)
      ? userData.funds
      : Array.isArray(userData.funds?.wallet)
        ? userData.funds.wallet
        : [];

    const totalValue = funds.reduce((s, x) => s + (x.value || 0), 0);
    const totalCost = funds.reduce((s, x) => s + (x.cost_usd || 0), 0);
    const totalPnl = totalValue - totalCost;
    const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

    const lockTotal = Number(lockSnapshot?.total) || 0;
    const sinceLockUsdTotal = lockTotal > 0 ? (totalValue - lockTotal) : 0;
    const sinceLockPctTotal = lockTotal > 0 ? (sinceLockUsdTotal / lockTotal) * 100 : 0;

    const positionDeltaValueNow = funds.reduce((s, r) => {
      const dAmt = Number(r.since_lock_amount) || 0;
      return s + dAmt * (Number(r.price) || 0);
    }, 0);

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">Total Value</Typography>
            <Typography variant="h6">
              ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="subtitle1">
              {viewMode === 'value' ? 'Profit/Loss' : 'Position Gain'}
            </Typography>
            {viewMode === 'value' ? (
              <Typography
                variant="body1"
                sx={{ color: sinceLockUsdTotal >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
              >
                ${formatUSD(sinceLockUsdTotal)} ({formatPct(sinceLockPctTotal)})
              </Typography>
            ) : (
              <Typography
                variant="body1"
                sx={{ color: positionDeltaValueNow >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                title="Sum of Δunits × current price across assets"
              >
                ${formatUSD(positionDeltaValueNow)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="subtitle1">Assets</Typography>
            <Typography variant="body1">
              {funds.length} {funds.length === 1 ? 'asset' : 'assets'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderLiveWalletTable = () => {
    const rows = walletRows?.rows || [];
    const totalUSDT = Number(walletRows?.totalUSDT || 0);
    const missing = rows.filter((r) => r.asset !== "USDT" && !(r.px > 0)).length;

    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ minWidth: 260 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Live Wallet
              </Typography>
              <Chip
                size="small"
                label="USDT value (live)"
                sx={{ fontWeight: 700 }}
                variant="outlined"
              />
              {missing > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${missing} missing prices`}
                  sx={{ fontWeight: 700 }}
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Values update automatically from your live market ticks.
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary">
              Total Value
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {fmtMoney(totalUSDT, 2)} USDT
            </Typography>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Asset</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Free</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Locked</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Price (USDT)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Value (USDT)</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => {
                const priceOk = r.asset === "USDT" || r.px > 0;
                return (
                  <TableRow
                    key={r.asset}
                    hover
                    sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 900 }}>{r.asset}</Typography>
                        {!priceOk && (
                          <Tooltip title="No live USDT price found for this asset yet">
                            <Chip size="small" label="no price" color="warning" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmtQty(r.free, 8)}</TableCell>
                    <TableCell align="right">{fmtQty(r.locked, 8)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 800 }}>{fmtQty(r.total, 8)}</Typography>
                    </TableCell>
                    <TableCell align="right">{priceOk ? fmtMoney(r.px, 6) : "—"}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 900 }}>{fmtMoney(r.valueUSDT, 2)}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No wallet data yet. Connect a real exchange to load balances.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {rows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: 900, borderTop: "1px solid", borderColor: "divider" }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, borderTop: "1px solid", borderColor: "divider" }}>
                    {fmtMoney(totalUSDT, 2)} USDT
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderFuturesSection = () => {
  if (!selectedExchange || selectedExchange.simulated) return null;

  const exType = (selectedExchange.exchange ?? selectedExchange.exchangeType ?? '').toLowerCase();
  if (!exType.includes('binance') || exType.includes('us')) return null;

  if (futuresLoading && !futuresAccount) {
    return (
      <Paper elevation={0} sx={{ mt: 2, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Skeleton height={40} width={200} />
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </Paper>
    );
  }

if (!futuresAccount && !futuresError) return null;
 
  // Show error state when futures fetch failed
  if (!futuresAccount && futuresError) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Futures Account
          </Typography>
          <Chip size="small" label="USDT-M" variant="outlined" color="warning" sx={{ fontWeight: 700 }} />
        </Box>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {futuresError}
        </Alert>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setFuturesRefetchKey(k => k + 1)}
          sx={{ mt: 1 }}
          startIcon={<Sync />}
        >
          Retry
        </Button>
      </Paper>
    );
  }
 
  const { totalMarginBalance, availableBalance, totalUnrealizedProfit, totalWalletBalance, positions } = futuresAccount;
  const unrealColor = totalUnrealizedProfit >= 0 ? 'success.main' : 'error.main';

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Futures Account
          </Typography>
          <Chip size="small" label="USDT-M" variant="outlined" color="warning" sx={{ fontWeight: 700 }} />
          {futuresLoading && <LinearProgress sx={{ width: 60, ml: 1 }} />}
        </Box>
        <Chip
          size="small"
          label={`${positions.length} open position${positions.length !== 1 ? 's' : ''}`}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Account Summary Cards */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: positions.length > 0 ? 3 : 0 }}>
          {[
            { label: 'Wallet Balance', value: fmtMoney(totalWalletBalance, 2), suffix: 'USDT' },
            { label: 'Margin Balance', value: fmtMoney(totalMarginBalance, 2), suffix: 'USDT' },
            { label: 'Available', value: fmtMoney(availableBalance, 2), suffix: 'USDT' },
            {
              label: 'Unrealized PnL',
              value: `${totalUnrealizedProfit >= 0 ? '+' : ''}${fmtMoney(totalUnrealizedProfit, 2)}`,
              suffix: 'USDT',
              color: unrealColor,
            },
          ].map((item, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                flex: '1 1 140px',
                p: 1.5,
                borderRadius: 1.5,
                minWidth: 140,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600, letterSpacing: 0.5 }}>
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  color: item.color || 'text.primary',
                }}
              >
                {item.value} <Typography component="span" variant="caption" color="text.secondary">{item.suffix}</Typography>
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Open Positions Table */}
        {positions.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Open Positions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Side</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Size</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Entry</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Mark</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Liq. Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>PnL (USDT)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Leverage</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Funding Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Next Funding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((pos) => {
                    const pnlColor = pos.unrealizedProfit >= 0 ? 'success.main' : 'error.main';
                    const funding = futuresFunding[pos.symbol];
                    const fundingRate = funding?.rate;
                    const fundingColor = fundingRate == null
                      ? 'text.secondary'
                      : fundingRate > 0 ? 'success.main' : fundingRate < 0 ? 'error.main' : 'text.secondary';

                    const liqDistPct = pos.liquidationPrice > 0 && pos.markPrice > 0
                      ? (Math.abs(pos.markPrice - pos.liquidationPrice) / pos.markPrice) * 100
                      : null;
                    const liqDanger = liqDistPct != null && liqDistPct < 5;

                    const minutesToFunding = funding?.nextTime
                      ? Math.max(0, Math.round((funding.nextTime - Date.now()) / 60_000))
                      : null;

                    return (
                      <TableRow key={pos.symbol} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{pos.symbol}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={pos.side}
                            sx={{
                              fontWeight: 700,
                              minWidth: 56,
                              bgcolor: pos.side === 'LONG' ? 'success.main' : 'error.main',
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontFamily: 'monospace' }}>
                            {fmtQty(pos.positionAmt, 6)}
                          </Typography>
                          {pos.notional > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              ~${fmtMoney(pos.notional, 0)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontFamily: 'monospace' }}>
                            {fmtMoney(pos.entryPrice, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontFamily: 'monospace' }}>
                            {fmtMoney(pos.markPrice, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={liqDistPct != null ? `${liqDistPct.toFixed(1)}% from mark price` : ''}
                            arrow
                          >
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                color: liqDanger ? 'error.main' : 'text.primary',
                                fontWeight: liqDanger ? 800 : 400,
                              }}
                            >
                              {pos.liquidationPrice > 0 ? fmtMoney(pos.liquidationPrice, 2) : '—'}
                            </Typography>
                          </Tooltip>
                          {liqDistPct != null && (
                            <Typography
                              variant="caption"
                              sx={{ color: liqDanger ? 'error.main' : 'text.secondary' }}
                            >
                              {liqDistPct.toFixed(1)}% away
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 800,
                              color: pnlColor,
                            }}
                          >
                            {pos.unrealizedProfit >= 0 ? '+' : ''}{fmtMoney(pos.unrealizedProfit, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={`${pos.leverage}x`}
                            variant="outlined"
                            color={
                              pos.leverage > 20 ? 'error'
                                : pos.leverage > 5 ? 'warning'
                                  : 'default'
                            }
                            sx={{ fontWeight: 700 }}
                          />
                          <Typography variant="caption" display="block" color="text.secondary">
                            {pos.marginType || 'cross'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {fundingRate != null ? (
                            <Tooltip
                              title={
                                fundingRate > 0
                                  ? 'Positive: longs pay shorts'
                                  : fundingRate < 0
                                    ? 'Negative: shorts pay longs'
                                    : 'Neutral'
                              }
                              arrow
                            >
                              <Typography
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  color: fundingColor,
                                  cursor: 'help',
                                }}
                              >
                                {(fundingRate * 100).toFixed(4)}%
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography color="text.secondary">—</Typography>
                          )}
                          {fundingRate != null && (
                            <Typography variant="caption" color="text.secondary">
                              {(fundingRate * 3 * 365 * 100).toFixed(1)}% ann.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {minutesToFunding != null ? (
                            <>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {new Date(funding.nextTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                in {minutesToFunding} min
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {positions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">
              No open futures positions.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

  const renderSimulatedHoldingsList = () => {
    if (!selectedExchange || !selectedExchange.simulated || isLoadingAssets) {
      return null;
    }

    const rows = userData.funds || [];
    const grandTotal = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

    const lockTotal = Number(lockSnapshot?.total) || 0;
    const sinceLockUsdTotal = lockTotal > 0 ? (grandTotal - lockTotal) : 0;

    const positionDeltaValueNow = rows.reduce((s, r) => {
      const dAmt = Number(r.since_lock_amount) || 0;
      const px = Number(r.price) || 0;
      return s + dAmt * px;
    }, 0);

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Holdings
          </Typography>

          {assetsLocked && lockSnapshot?.per && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Locked on {lockSnapshot?.ts ? moment.utc(lockSnapshot.ts).format('MMM D, YYYY • HH:mm [UTC]') : '—'}
              {' '}•{' '}
              Total @ lock: ${formatUSD(lockSnapshot?.total || 0)}
            </Typography>
          )}

          {assets.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              No assets in this exchange yet. Click "Add Holding" to get started.
            </Typography>
          ) : (
            <List>
              {rows.map((row) => {
                const symbol = row.currency;
                const amount = Number(row.amount) || 0;
                const price = Number(row.price) || 0;
                const usdValue = Number(row.value) || 0;

                const lockPer = lockSnapshot?.per?.[symbol];
                const showLock = assetsLocked && lockPer && Number.isFinite(lockPer.amount);
                const cryptoInfo = cryptoOptions.find(c => c.symbol === symbol) || { name: symbol, icon: null };

                return (
                  <React.Fragment key={row.id || symbol}>
                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 240 }}>
                        <Avatar src={cryptoInfo.icon} alt={symbol} sx={{ width: 40, height: 40, mr: 2 }}>
                          {!cryptoInfo.icon && symbol.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1">
                            {cryptoInfo.name}{' '}
                            <Typography component="span" variant="subtitle2" color="text.secondary">
                              (${formatUSD(price)})
                            </Typography>
                          </Typography>
                          {showLock && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              Initial @ lock: {formatCrypto(lockPer.amount, symbol)} @ ${formatUSD(lockPer.price)} • ${formatUSD(lockPer.value)}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {symbol}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 220 }}>
                        <Typography variant="subtitle1">{formatCrypto(amount, symbol)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${formatUSD(usdValue)}
                        </Typography>
                      </Box>

                      <IconButton
                        edge="end"
                        aria-label="delete"
                        disabled={assetsLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (assetsLocked) {
                            toast.error("Assets are locked. You cannot remove holdings.");
                            return;
                          }
                          setDeleteConfirm({ open: true, id: row.id });
                        }}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <Delete />
                      </IconButton>
                    </ListItem>
                    <Divider variant="middle" component="li" />
                  </React.Fragment>
                );
              })}

              <ListItem sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ${formatUSD(grandTotal)}
                  </Typography>
                </Box>
              </ListItem>

              <ListItem sx={{ pt: 0, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {viewMode === 'value' ? 'After Lock Net Profit' : 'After Lock Net Position'}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: (viewMode === 'value' ? sinceLockUsdTotal : positionDeltaValueNow) >= 0
                        ? 'success.main'
                        : 'error.main',
                      fontWeight: 700
                    }}
                    title={viewMode === 'value'
                      ? 'Current total - lock total'
                      : 'Sum of Δunits × current price across assets'}
                  >
                    ${formatUSD(viewMode === 'value' ? sinceLockUsdTotal : positionDeltaValueNow)}
                  </Typography>
                </Box>
              </ListItem>
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  // Main Render
  if (!isLoadedPrice) {
    return <Loading />;
  }

  return (
    <div>
      <Toolbar button={<Logout />} />

      <ToastContainer
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        position={toast.POSITION.TOP_RIGHT}
        style={{ textAlign: "center" }}
      />

      {uiState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uiState.errorInfo}
        </Alert>
      )}

      <Wrapper>
        {isLoadingExchanges ? (
          <Box sx={{ width: '100%' }}>
            <Skeleton height={56} />
            <Skeleton height={56} />
            <Skeleton height={56} />
          </Box>
        ) : (
          <>
            {renderConnectionForm()}
            {renderExchangeList()}
          </>
        )}

        {selectedExchange && (
          <>
            {renderPortfolioActions()}
            {renderPortfolioSummary()}
            {isLoadingAssets ? (
              <Box sx={{ width: '100%' }}>
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={56} />
              </Box>
            ) : (
              <>

              {selectedExchange?.simulated
  ? renderSimulatedHoldingsList()
  : (
    <>
      {renderLiveWalletTable()}
      {renderFuturesSection()}
    </>
  )
}
              </>
            )}
          </>
        )}

        {renderAddHoldingDialog()}
        {renderDeleteConfirm()}
        {renderKeyModal()}
      </Wrapper>

      <Footer />
    </div>
  );
};

export default Portfolio;